import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Users, Loader2 } from "lucide-react";
import { teamsAPI } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface TeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamCreated: () => void;
  userId: string;
  editTeam?: {
    id: string;
    name: string;
    description?: string;
  } | null;
}

export const TeamModal = ({ open, onOpenChange, onTeamCreated, userId, editTeam }: TeamModalProps) => {
  const [teamName, setTeamName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (editTeam) {
      setTeamName(editTeam.name);
      setDescription(editTeam.description || "");
    } else {
      setTeamName("");
      setDescription("");
    }
  }, [editTeam, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!teamName.trim()) {
      toast({
        title: t("common.error"),
        description: t("teamModal.nameRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      let response;
      if (editTeam) {
        response = await teamsAPI.updateTeam(editTeam.id, {
          name: teamName.trim(),
          description: description.trim() || undefined,
        });
      } else {
        response = await teamsAPI.createTeam({
          name: teamName.trim(),
          description: description.trim() || undefined,
        });
      }

      if (response.success) {
        toast({
          title: t("teamModal.successTitle"),
          description: editTeam 
            ? t("teamModal.updateSuccessDesc", { teamName })
            : t("teamModal.successDesc", { teamName }),
        });

        // Reset form
        setTeamName("");
        setDescription("");
        onOpenChange(false);
        onTeamCreated();
      } else {
        throw new Error(response.message || 'Failed to save team');
      }

    } catch (error: any) {
      console.error("Error saving team:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "teamModal.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card/95 backdrop-blur-xl border-border/50">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <DialogTitle className="text-2xl">
              {editTeam ? t("teamModal.editTeam") : t("teamModal.createNew")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            {t("teamModal.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="teamName" className="text-foreground font-semibold">
                {t("teamModal.teamName")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="teamName"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder={t("teamModal.placeholder")}
                className="bg-background border-border/50 focus:border-primary"
                maxLength={100}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground font-semibold">
                {t("teamModal.descriptionLabel")} <span className="text-muted-foreground text-xs">({t("teamModal.optional")})</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("teamModal.descPlaceholder")}
                className="bg-background border-border/50 focus:border-primary min-h-[100px] resize-none"
                maxLength={500}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                {description.length}/500 {t("teamModal.characters")}
              </p>
            </div>

            {!editTeam && (
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
                <h4 className="font-semibold text-sm text-foreground mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  {t("teamModal.whatHappens")}
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1.5">
                  <li>• {t("teamModal.youllBeAdmin")}</li>
                  <li>• {t("teamModal.inviteMembers")}</li>
                  <li>• {t("teamModal.compareStrengths")}</li>
                  <li>• {t("teamModal.chatTeam")}</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t("teamModal.cancel")}
            </Button>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 shadow-glow"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {editTeam ? t("teamModal.updating") : t("teamModal.creating")}
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  {editTeam ? t("teamModal.update") : t("teamModal.create")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
